import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useModals, useModalSuccess } from '@/contexts/ModalContext';
import { Header, DashboardLayout } from '@/components/shared';
import { EditUserModal, AssignUserModal } from '@/components/modals';
import { getApiUrl } from '@/config/api';
import { TabNavigation } from '@/components/shared';
import {
    User,
    UpdateUserData,
    AssignUserData,
    Overview,
    TeamDetailed,
    Organization
} from '@/types';
import { OverviewTab, UsersTab, TeamsTab, OrganizationsTab } from './index';
import { generateCommands } from '@/config/commands';

type ActiveTab = 'overview' | 'users' | 'teams' | 'organizations';

/**
 * Elegant AdminDashboard Component with modern design and beautiful UI.
 * Features sophisticated system management, comprehensive analytics, and intuitive administration tools.
 * Provides complete oversight of users, teams, organizations with advanced filtering and management capabilities.
 */
const AdminDashboard: React.FC = () => {
    const [overview, setOverview] = useState<Overview | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [teams, setTeams] = useState<TeamDetailed[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [searchParams] = useSearchParams();
    const activeTab = (searchParams.get('tab') || 'overview') as ActiveTab;
    const [showEditUserModal, setShowEditUserModal] = useState<{ user: User } | false>(false);
    const [showAssignUserModal, setShowAssignUserModal] = useState<{ user: User } | false>(false);
    const [copiedMessage, setCopiedMessage] = useState<string>('');
    const [userInfo, setUserInfo] = useState<User | null>(null);
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const { openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal } = useModals();

    useEffect(() => {
        // Check if user is authenticated and is admin
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user info from localStorage
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user: User = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }

        fetchDashboardData();
    }, [navigate]);

    const fetchDashboardData = async (): Promise<void> => {
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        try {
            setLoading(true);
            const [overviewRes, usersRes, teamsRes, orgsRes] = await Promise.all([
                fetch(getApiUrl('/api/teams/admin/overview/'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(getApiUrl('/api/teams/admin/users/'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(getApiUrl('/api/teams/admin/teams-detailed/'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(getApiUrl('/api/teams/organizations/'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (!overviewRes.ok || !usersRes.ok || !teamsRes.ok || !orgsRes.ok) {
                const status = [overviewRes, usersRes, teamsRes, orgsRes].find(res => !res.ok)?.status;
                if (status === 401 || status === 403) {
                    // Token expired or user not authorized - redirect to login
                    localStorage.removeItem('access');
                    localStorage.removeItem('refresh');
                    navigate('/login');
                    return;
                }
                throw new Error('Failed to fetch admin data');
            }

            const [overviewData, usersData, teamsData, orgsData] = await Promise.all([
                overviewRes.json(),
                usersRes.json(),
                teamsRes.json(),
                orgsRes.json()
            ]);

            setOverview(overviewData);
            setUsers(usersData);
            setTeams(teamsData);

            // Handle paginated organizations response
            const organizations = Array.isArray(orgsData)
                ? orgsData
                : (orgsData.results || orgsData.data || []);
            setOrganizations(organizations);
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    useModalSuccess(() => fetchDashboardData());

    /**
     * Handle user logout
     */
    const handleLogout = (): void => {
        localStorage.removeItem('access');
        localStorage.removeItem('refresh');
        localStorage.removeItem('user_info');
        navigate('/login');
    };

    /**
     * Copy text to clipboard
     */
    const copyToClipboard = async (text: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(text);
            setCopiedMessage(text);
            setTimeout(() => setCopiedMessage(''), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    /**
     * Handle organization creation (optimistic UI update)
     * @param orgData - Organization data from the form
     * @returns Promise<boolean> - Success status
     */
    const handleCreateOrganization = async (orgData: { name: string; description: string }): Promise<boolean> => {
        // Create temporary org with placeholder ID
        const tempOrg: Organization = {
            id: Date.now(), // Temporary ID
            name: orgData.name,
            description: orgData.description,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            teams: []
        };
        
        // Optimistically add to UI immediately
        setOrganizations(prev => [...prev, tempOrg]);
        
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                alert('Authentication required. Please log in again.');
                navigate('/login');
                return false;
            }

            const response = await fetch(getApiUrl('/api/teams/admin/create-organization/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(orgData)
            });

            if (response.ok) {
                const result = await response.json();

                // Replace temp org with real one from API response
                setOrganizations(prev => 
                    prev.map(org => org.id === tempOrg.id ? result : org)
                );

                // Optionally refresh overview data for updated counts
                // (but don't refetch orgs to avoid race conditions)
                const overviewToken = localStorage.getItem('access');
                if (overviewToken) {
                    fetch(getApiUrl('/api/teams/admin/overview/'), {
                        headers: { 'Authorization': `Bearer ${overviewToken}` }
                    })
                    .then(res => res.ok ? res.json() : null)
                    .then(data => data && setOverview(data))
                    .catch(err => console.error('Error refreshing overview:', err));
                }

                return true;
            } else {
                const error = await response.json();
                console.error('Failed to create organization:', error);
                alert(`Failed to create organization: ${error.error || 'Unknown error'}`);
                
                // Remove temp org on failure
                setOrganizations(prev => prev.filter(org => org.id !== tempOrg.id));
                return false;
            }
        } catch (error) {
            console.error('Error creating organization:', error);
            alert('Network error while creating organization. Please try again.');
            
            // Remove temp org on error
            setOrganizations(prev => prev.filter(org => org.id !== tempOrg.id));
            return false;
        }
    };

    /**
     * Handle team creation (optimistic UI update)
     * @param teamData - Team data from the form
     * @returns Promise<boolean> - Success status
     */
    const handleCreateTeam = async (teamData: { organization_id: number; sport: string; gender: 'mixed' | 'male' | 'female' }): Promise<boolean> => {
        // Find the organization
        const org = organizations.find(o => o.id === teamData.organization_id);
        
        // Create temporary team with placeholder data
        const tempTeam: TeamDetailed = {
            id: Date.now(), // Temporary ID
            name: `${org?.name || 'New'} ${teamData.sport} Team`,
            organization: org || { id: teamData.organization_id, name: 'Organization', description: '', created_at: '', updated_at: '' },
            description: '',
            sport: teamData.sport,
            gender: teamData.gender,
            coach_code: 'LOADING...',
            athlete_code: 'LOADING...',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            memberships: []
        };
        
        // Optimistically add to UI immediately
        setTeams(prev => [...prev, tempTeam]);
        
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                alert('Authentication required. Please log in again.');
                navigate('/login');
                return false;
            }

            const response = await fetch(getApiUrl('/api/teams/admin/create-team/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(teamData)
            });

            if (response.ok) {
                const result = await response.json();

                // Set the team created data for the success modal
                setTeamCreatedData(result);

                // Replace temp team with real one from API response
                // The API returns { team: {...}, coach_code: '...', athlete_code: '...' }
                const realTeam: TeamDetailed = {
                    ...result.team,
                    coach_code: result.coach_code,
                    athlete_code: result.athlete_code,
                    memberships: result.team.memberships || []
                };
                
                setTeams(prev => 
                    prev.map(team => team.id === tempTeam.id ? realTeam : team)
                );

                // Optionally refresh overview data for updated counts
                // (but don't refetch teams to avoid race conditions)
                const overviewToken = localStorage.getItem('access');
                if (overviewToken) {
                    fetch(getApiUrl('/api/teams/admin/overview/'), {
                        headers: { 'Authorization': `Bearer ${overviewToken}` }
                    })
                    .then(res => res.ok ? res.json() : null)
                    .then(data => data && setOverview(data))
                    .catch(err => console.error('Error refreshing overview:', err));
                }

                return true;
            } else {
                const error = await response.json();
                console.error('Failed to create team:', error);
                alert(`Failed to create team: ${error.error || 'Unknown error'}`);
                
                // Remove temp team on failure
                setTeams(prev => prev.filter(team => team.id !== tempTeam.id));
                return false;
            }
        } catch (error) {
            console.error('Error creating team:', error);
            alert('Network error while creating team. Please try again.');
            
            // Remove temp team on error
            setTeams(prev => prev.filter(team => team.id !== tempTeam.id));
            return false;
        }
    };

    /**
     * Handle organization deletion (optimistic UI update)
     * @param orgId - Organization ID to delete
     */
    const handleDeleteOrganization = async (orgId: number): Promise<void> => {
        // Optimistically remove from UI immediately
        setOrganizations(prev => prev.filter(org => org.id !== orgId));
        
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                alert('Authentication required. Please log in again.');
                navigate('/login');
                return;
            }

            const response = await fetch(getApiUrl(`/api/teams/admin/delete-organization/${orgId}/`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                alert(`Organization deleted successfully! ${result.teams_deleted} teams and ${result.memberships_removed} memberships removed.`);
            } else {
                const error = await response.json();
                console.error('Failed to delete organization:', error);
                alert(`Failed to delete organization: ${error.error || 'Unknown error'}`);
                // Refresh to restore correct state
                await fetchDashboardData();
            }
        } catch (error) {
            console.error('Error deleting organization:', error);
            alert('Network error while deleting organization. Please try again.');
            // Refresh to restore correct state
            await fetchDashboardData();
        }
    };

    /**
     * Handle team deletion (optimistic UI update)
     * @param teamId - Team ID to delete
     */
    const handleDeleteTeam = async (teamId: number): Promise<void> => {
        // Optimistically remove from UI immediately
        setTeams(prev => prev.filter(team => team.id !== teamId));
        
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                alert('Authentication required. Please log in again.');
                navigate('/login');
                return;
            }

            const response = await fetch(getApiUrl(`/api/teams/admin/delete-team/${teamId}/`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const result = await response.json();
                alert('Team deleted successfully!');
            } else {
                const error = await response.json();
                console.error('Failed to delete team:', error);
                alert(`Failed to delete team: ${error.error || 'Unknown error'}`);
                // Refresh to restore correct state
                await fetchDashboardData();
            }
        } catch (error) {
            console.error('Error deleting team:', error);
            alert('Network error while deleting team. Please try again.');
            // Refresh to restore correct state
            await fetchDashboardData();
        }
    };

    /**
     * Handle sending email digest to selected coaches
     * @param coachIds - Array of coach user IDs to send digests to
     * @returns Promise with success status and message
     */
    const handleSendEmailDigest = async (coachIds: number[]): Promise<{ success: boolean; message: string }> => {
        try {
            const token = localStorage.getItem('access');
            if (!token) {
                alert('Authentication required. Please log in again.');
                navigate('/login');
                return { success: false, message: 'Authentication required' };
            }

            const response = await fetch(getApiUrl('/api/teams/admin/send-digest/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ coach_ids: coachIds })
            });

            const result = await response.json();

            if (response.ok) {
                return { 
                    success: true, 
                    message: result.message || `Sent ${result.success_count} digest(s)` 
                };
            } else {
                console.error('Failed to send email digest:', result);
                return { 
                    success: false, 
                    message: result.error || 'Failed to send email digest' 
                };
            }
        } catch (error) {
            console.error('Error sending email digest:', error);
            return { 
                success: false, 
                message: 'Network error while sending email digest' 
            };
        }
    };

    const updateUser = async (userData: UpdateUserData): Promise<boolean> => {
        const token = localStorage.getItem('access');
        if (!showEditUserModal || typeof showEditUserModal === 'boolean') {
            alert('No user selected for editing');
            return false;
        }

        try {
            const response = await fetch(getApiUrl(`/api/teams/admin/update-user/${showEditUserModal.user.id}/`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(userData),
            });

            if (!response.ok) {
                let message = `Failed to update user (HTTP ${response.status})`;
                try {
                    const ct = response.headers.get('content-type') || '';
                    if (ct.includes('application/json')) {
                        const errorData = await response.json();
                        message = errorData.error || errorData.detail || errorData.message || message;
                    } else {
                        const text = await response.text();
                        message = text || message;
                    }
                } catch { }
                if (response.status === 401) {
                    localStorage.removeItem('access');
                    localStorage.removeItem('refresh');
                    navigate('/login');
                }
                throw new Error(message);
            }

            return true;
        } catch (error) {
            console.error('Error updating user:', error);
            throw (error instanceof Error ? error : new Error('Network error while updating user. Please try again.'));
        }
    };

    const assignUserToTeam = async (assignmentData: AssignUserData): Promise<boolean> => {
        const token = localStorage.getItem('access');
        try {
            const response = await fetch(getApiUrl('/api/teams/admin/assign-user-to-team/'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(assignmentData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || 'Failed to assign user to team');
                return false;
            }

            return true;
        } catch (error) {
            console.error('Error assigning user to team:', error);
            alert('Network error while assigning user to team. Please try again.');
            return false;
        }
    };

    /**
     * Delete a user by ID
     * @param userId - The ID of the user to delete
     * @returns Promise<boolean> - Success status
     */
    const deleteUser = async (userId: number): Promise<boolean> => {
        const token = localStorage.getItem('access');
        if (!token) {
            alert('Authentication required. Please log in again.');
            navigate('/login');
            return false;
        }
        try {
            const response = await fetch(getApiUrl(`/api/teams/admin/delete-user/${userId}/`), {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                alert(errorData.error || 'Failed to delete user');
                return false;
            }
            return true;
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Network error while deleting user. Please try again.');
            return false;
        }
    };

    const commands = useMemo(() => generateCommands({
        navigate,
        openCreateUserModal,
        openInviteUserModal,
        openCreateTeamModal,
        openCreateOrgModal,
        fetchDashboardData
    }), [navigate, openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal]);

    return (
        <div className="min-h-screen cgm-bg-primary">
            {loading ?
                <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center space-y-4">
                        <div className="relative">
                            <div className={`w-20 h-20 border-4 ${isDark ? 'border-red-800' : 'border-red-200'} rounded-full animate-spin`}>
                                <div className={`absolute top-0 left-0 w-20 h-20 border-4 border-transparent ${isDark ? 'border-t-red-400' : 'border-t-red-600'} rounded-full animate-spin`}></div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h3 className="text-xl font-semibold cgm-text-primary">Loading Admin Dashboard</h3>
                            <p className="cgm-text-secondary">Gathering system analytics...</p>
                        </div>
                    </div>
                </div> :
                error ?
                    <div className="max-w-md w-full">
                        <div className="cgm-card p-8 text-center">
                            <div className="w-16 h-16 cgm-alert-error rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 cgm-icon-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold cgm-icon-error mb-4">Something went wrong</h2>
                            <p className="cgm-text-secondary mb-6 leading-relaxed">{error}</p>
                            <button
                                onClick={() => window.location.reload()}
                                className="cgm-button px-6 py-3 rounded-xl font-medium transition-all duration-200 transform hover:scale-105"
                            >
                                Try Again
                            </button>
                        </div>
                    </div> :
                    <>
                        {/* Navigation */}
                        <Header
                            userInfo={userInfo}
                            isDark={isDark}
                            commands={commands}
                        />

                        {/* Main Content */}
                        <DashboardLayout>
                            {/* Navigation Tabs */}
                            <TabNavigation
                                tabs={[
                                    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
                                    { id: 'users', label: 'Users', icon: 'Users' },
                                    { id: 'teams', label: 'Teams', icon: 'Trophy' },
                                    { id: 'organizations', label: 'Organizations', icon: 'Building2' }
                                ]}
                                defaultTab="overview"
                                paramName="tab"
                            />

                            {/* Overview Section */}
                            {activeTab === 'overview' && (
                                <OverviewTab overview={overview} isDark={isDark} />
                            )}

                            {/* Users Tab */}
                            {activeTab === 'users' && (
                                <UsersTab
                                    users={users}
                                    isDark={isDark}
                                    setShowEditUserModal={setShowEditUserModal}
                                    setShowAssignUserModal={setShowAssignUserModal}
                                    deleteUser={deleteUser}
                                    fetchDashboardData={fetchDashboardData}
                                />
                            )}

                        {/* Teams Tab */}
                        {activeTab === 'teams' && (
                            <TeamsTab
                                teams={teams}
                                isDark={isDark}
                                copiedMessage={copiedMessage}
                                copyToClipboard={copyToClipboard}
                                onDeleteTeam={handleDeleteTeam}
                                onSendEmailDigest={handleSendEmailDigest}
                            />
                        )}

                        {/* Organizations Tab */}
                        {activeTab === 'organizations' && (
                            <OrganizationsTab
                                organizations={organizations}
                                isDark={isDark}
                                onDeleteOrganization={handleDeleteOrganization}
                            />
                        )}
                        </DashboardLayout>

                        {showEditUserModal && (
                            <EditUserModal
                                onClose={() => setShowEditUserModal(false)}
                                user={showEditUserModal.user}
                                onUpdate={async (userData) => {
                                    const success = await updateUser(userData);
                                    if (success) {
                                        await fetchDashboardData();
                                    }
                                    return success;
                                }}
                            />
                        )}

                        {showAssignUserModal && (
                            <AssignUserModal
                                onClose={() => setShowAssignUserModal(false)}
                                user={showAssignUserModal.user}
                                teams={teams}
                                onAssign={async (assignmentData) => {
                                    const success = await assignUserToTeam(assignmentData);
                                    if (success) {
                                        await fetchDashboardData();
                                    }
                                    return success;
                                }}
                            />
                        )}
                    </>
            }
        </div>

    );
};

export default AdminDashboard; 